import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { Kbd } from '@/components/ui/kbd'
import { Button } from '@/components/ui/button'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'
import {
  fileSearchHref,
  initialSearchQuery,
  parseSearchQuery,
  repositorySearchPrefix,
  type RepositorySearchScope,
} from '@/lib/search'
import {
  readRecentSearchVisits,
  recordRecentSearchVisit,
  searchVisitFromPathname,
  type SearchVisit,
} from '@/lib/search-history'
import {
  Building2,
  BookMarked,
  CircleHelp,
  File,
  LockKeyhole,
  UserRound,
} from 'lucide-react'
import { useOnboardingChrome } from '@/components/onboarding/chrome-context'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

interface OwnerResult {
  type: 'user' | 'organization'
  id: string
  handle: string
  name: string
  href: string
}

interface RepositoryResult {
  id: number
  namespace: string
  name: string
  description: string | null
  isPrivate: boolean
  href: string
}

interface FileResult {
  owner: string
  repo: string
  ref: string
  path: string
}

interface SearchResponse {
  owners?: OwnerResult[]
  repositories?: RepositoryResult[]
  files?: FileResult[]
}

interface RepositorySuggestion extends RepositoryResult {
  reason: string
  activityAt: string | null
  activityLabel: string | null
}

interface FileSuggestion extends FileResult {
  reason: string
}

interface SuggestionResponse {
  repositories?: RepositorySuggestion[]
  files?: FileSuggestion[]
}

const EMPTY_RESULTS: Required<SearchResponse> = {
  owners: [],
  repositories: [],
  files: [],
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return (
    target.isContentEditable ||
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT'
  )
}

function resultDescription(path: string): { directory: string; name: string } {
  const parts = path.split('/')
  const name = parts.pop() ?? path
  return { directory: parts.join('/'), name }
}

export function GlobalSearch({
  scope,
  showSyntaxHelp = false,
}: {
  scope: RepositorySearchScope | null
  showSyntaxHelp?: boolean
}) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const inputRef = useRef<HTMLInputElement>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState(() => initialSearchQuery(scope))
  const [results, setResults] = useState(EMPTY_RESULTS)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [recentVisits, setRecentVisits] = useState<SearchVisit[]>([])
  const [suggestions, setSuggestions] = useState<Required<SuggestionResponse>>({
    repositories: [],
    files: [],
  })
  const [suggestionsLoading, setSuggestionsLoading] = useState(false)
  const parsedQuery = useMemo(() => parseSearchQuery(query), [query])

  const setListNode = useCallback(
    (node: HTMLDivElement | null) => {
      listRef.current = node
      const input = inputRef.current
      if (!input) return
      if (open && node?.id) input.setAttribute('aria-controls', node.id)
      else input.removeAttribute('aria-controls')
    },
    [open],
  )

  // cmdk models its input as an always-expanded combobox. Kilio mounts the
  // results in a conditional popover, so keep the DOM ARIA state aligned with
  // whether that list is actually present.
  useLayoutEffect(() => {
    const input = inputRef.current
    if (!input) return
    input.setAttribute('aria-expanded', String(open))
    if (open && listRef.current?.id) input.setAttribute('aria-controls', listRef.current.id)
    else input.removeAttribute('aria-controls')
  }, [open])
  const hasResults =
    results.owners.length > 0 || results.repositories.length > 0 || results.files.length > 0
  const visibleVisits = useMemo(() => {
    const repoPrefix = scope ? `/${scope.owner}/${scope.repo}` : null
    return recentVisits
      .filter((visit) => visit.href !== pathname)
      .filter(
        (visit) =>
          !repoPrefix || visit.href === repoPrefix || visit.href.startsWith(`${repoPrefix}/`),
      )
      .slice(0, 4)
  }, [pathname, recentVisits, scope])
  const syntaxExamples = useMemo(() => {
    const prefix = scope ? `${repositorySearchPrefix(scope)} ` : ''
    return [
      {
        label: 'TypeScript files in src',
        value: `${prefix}language:typescript path:src`,
      },
      {
        label: 'Exact phrase, excluding tests',
        value: `${prefix}"exact phrase" NOT path:test`,
      },
    ]
  }, [scope])

  const chrome = useOnboardingChrome()
  const openSearch = useCallback(() => {
    setOpen(true)
    window.requestAnimationFrame(() => inputRef.current?.focus())
  }, [])

  useEffect(() => chrome?.registerSearchOpener(openSearch), [chrome, openSearch])

  useEffect(() => {
    if (!open) setQuery(initialSearchQuery(scope))
  }, [open, scope])

  useEffect(() => {
    const storage = typeof window === 'undefined' ? undefined : window.localStorage
    const visit = searchVisitFromPathname(pathname)
    setRecentVisits(
      visit ? recordRecentSearchVisit(visit, storage) : readRecentSearchVisits(storage),
    )
  }, [pathname])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const commandShortcut = event.key.toLocaleLowerCase() === 'k' && (event.metaKey || event.ctrlKey)
      const slashShortcut = event.key === '/' && !event.metaKey && !event.ctrlKey && !event.altKey
      if (slashShortcut && document.querySelector('[data-issue-search]')) return
      if ((!commandShortcut && !slashShortcut) || isEditableTarget(event.target)) return
      event.preventDefault()
      openSearch()
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [openSearch])

  useEffect(() => {
    if (!open || !parsedQuery.hasSearchTerms || parsedQuery.error) {
      setResults(EMPTY_RESULTS)
      setLoading(false)
      setError(parsedQuery.error ?? '')
      return
    }

    let cancelled = false
    setLoading(true)
    setError('')

    const timer = window.setTimeout(() => {
      const params = new URLSearchParams({ q: parsedQuery.query })
      if (
        scope?.ref &&
        parsedQuery.scope?.owner === scope.owner &&
        parsedQuery.scope.repo === scope.repo
      ) {
        params.set('ref', scope.ref)
      }

      void api
        .get<SearchResponse>(`/api/search?${params.toString()}`)
        .then((next) => {
          if (cancelled) return
          setResults({
            owners: next.owners ?? [],
            repositories: next.repositories ?? [],
            files: next.files ?? [],
          })
        })
        .catch((searchError: unknown) => {
          if (cancelled) return
          setResults(EMPTY_RESULTS)
          setError(searchError instanceof Error ? searchError.message : 'Search failed')
        })
        .finally(() => {
          if (!cancelled) setLoading(false)
        })
    }, 180)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [open, parsedQuery, scope])

  useEffect(() => {
    if (!open || parsedQuery.hasSearchTerms || parsedQuery.error) {
      setSuggestionsLoading(false)
      return
    }

    let cancelled = false
    setSuggestionsLoading(true)
    const params = new URLSearchParams()
    if (scope) {
      params.set('repo', `${scope.owner}/${scope.repo}`)
      if (scope.ref) params.set('ref', scope.ref)
    }
    const suffix = params.size > 0 ? `?${params.toString()}` : ''

    void api
      .get<SuggestionResponse>(`/api/search/suggestions${suffix}`)
      .then((next) => {
        if (cancelled) return
        setSuggestions({
          repositories: next.repositories ?? [],
          files: next.files ?? [],
        })
      })
      .catch(() => {
        if (!cancelled) setSuggestions({ repositories: [], files: [] })
      })
      .finally(() => {
        if (!cancelled) setSuggestionsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, parsedQuery.error, parsedQuery.hasSearchTerms, scope])

  const selectResult = (href: string) => {
    setOpen(false)
    navigate(href)
  }

  const applySyntaxExample = (value: string) => {
    setQuery(value)
    window.requestAnimationFrame(() => inputRef.current?.focus())
  }

  const triggerLabel = scope ? repositorySearchPrefix(scope) : 'Search Kilio'
  const triggerAriaLabel = scope ? `Search ${triggerLabel}` : triggerLabel
  return (
    <div className="w-[36vw] min-w-32 sm:w-[42vw] sm:max-w-md">
      <Command shouldFilter={false} className="h-auto overflow-visible bg-transparent">
        <Popover
          open={open}
          onOpenChange={(nextOpen) => {
            setOpen(nextOpen)
            if (nextOpen) window.requestAnimationFrame(() => inputRef.current?.focus())
          }}
        >
          <PopoverAnchor asChild>
            <div
              ref={anchorRef}
              className="relative border bg-background has-[input:focus-visible]:border-ring has-[input:focus-visible]:ring-1 has-[input:focus-visible]:ring-ring/50"
              onClick={openSearch}
            >
              <CommandInput
                ref={inputRef}
                value={query}
                onValueChange={setQuery}
                onFocus={() => setOpen(true)}
                onClick={() => setOpen(true)}
                placeholder="Search Kilio"
                aria-label={triggerAriaLabel}
                aria-expanded={open}
                className={cn(showSyntaxHelp ? 'pr-16' : 'pr-8')}
                wrapperClassName="border-b-0"
              />
              <div className="absolute top-1/2 right-1.5 flex -translate-y-1/2 items-center gap-1">
                {showSyntaxHelp ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button asChild variant="ghost" size="icon-xs">
                        <Link
                          to="/docs/search-syntax"
                          aria-label="Search syntax documentation"
                          data-tour="search-syntax-help"
                          onClick={(event) => {
                            event.stopPropagation()
                            setOpen(false)
                          }}
                        >
                          <CircleHelp />
                        </Link>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">Search syntax</TooltipContent>
                  </Tooltip>
                ) : null}
                <Kbd>/</Kbd>
              </div>
            </div>
          </PopoverAnchor>
          <PopoverContent
            aria-label="Search results"
            align="center"
            sideOffset={6}
            className="w-[min(32rem,calc(100vw-1.5rem))] p-0 sm:w-[42vw] sm:max-w-md"
            onOpenAutoFocus={(event) => event.preventDefault()}
            onInteractOutside={(event) => {
              if (anchorRef.current?.contains(event.target as Node)) event.preventDefault()
            }}
          >
            <CommandList ref={setListNode} className="max-h-[min(28rem,65vh)]">
              {!parsedQuery.hasSearchTerms && !parsedQuery.error ? (
                <>
                  {visibleVisits.length > 0 ? (
                    <CommandGroup heading="Recently viewed">
                      {visibleVisits.map((visit) => (
                        <CommandItem
                          key={visit.href}
                          value={`visit:${visit.href}`}
                          onSelect={() => selectResult(visit.href)}
                        >
                          {visit.type === 'file' ? (
                            <File />
                          ) : visit.type === 'repository' ? (
                            <BookMarked />
                          ) : (
                            <UserRound />
                          )}
                          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                            <span className="truncate font-medium">{visit.label}</span>
                            <span className="truncate text-muted-foreground">
                              {visit.description}
                            </span>
                          </span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ) : null}

                  {scope && suggestions.files.length > 0 ? (
                    <CommandGroup heading="Recently changed">
                      {suggestions.files.map((file) => {
                        const { directory, name } = resultDescription(file.path)
                        return (
                          <CommandItem
                            key={`suggested:${file.owner}/${file.repo}/${file.ref}/${file.path}`}
                            value={`suggested-file:${file.path}`}
                            onSelect={() => selectResult(fileSearchHref(file))}
                          >
                            <File />
                            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                              <span className="truncate font-medium">{name}</span>
                              <span className="truncate text-muted-foreground">
                                {directory || file.reason}
                              </span>
                            </span>
                          </CommandItem>
                        )
                      })}
                    </CommandGroup>
                  ) : null}

                  {!scope && suggestions.repositories.length > 0 ? (
                    <CommandGroup heading="Suggested repositories">
                      {suggestions.repositories.map((repo) => (
                        <CommandItem
                          key={`suggested:${repo.id}`}
                          value={`suggested-repository:${repo.namespace}/${repo.name}`}
                          onSelect={() => selectResult(repo.href)}
                        >
                          <BookMarked />
                          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                            <span className="flex min-w-0 items-center gap-1.5">
                              <span className="truncate font-medium">
                                {repo.namespace}/{repo.name}
                              </span>
                              {repo.isPrivate ? (
                                <LockKeyhole aria-label="Private repository" />
                              ) : null}
                            </span>
                            <span className="truncate text-muted-foreground">
                              {repo.reason}
                              {repo.activityLabel ? ` · ${repo.activityLabel}` : ''}
                            </span>
                          </span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ) : null}

                  {(visibleVisits.length > 0 ||
                    suggestions.files.length > 0 ||
                    suggestions.repositories.length > 0) && <CommandSeparator />}

                  {showSyntaxHelp ? (
                    <CommandGroup heading="Search syntax">
                      {syntaxExamples.map((example) => (
                        <CommandItem
                          key={example.value}
                          value={`syntax:${example.value}`}
                          onSelect={() => applySyntaxExample(example.value)}
                        >
                          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                            <span className="truncate font-medium">{example.label}</span>
                            <code className="truncate text-[11px] text-muted-foreground">
                              {example.value}
                            </code>
                          </span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ) : null}

                  {suggestionsLoading &&
                  visibleVisits.length === 0 &&
                  suggestions.files.length === 0 &&
                  suggestions.repositories.length === 0 ? (
                    <div
                      className="px-3 py-2 text-center text-xs text-muted-foreground"
                      role="status"
                    >
                      Loading activity…
                    </div>
                  ) : null}
                  {!showSyntaxHelp &&
                  !suggestionsLoading &&
                  visibleVisits.length === 0 &&
                  suggestions.files.length === 0 &&
                  suggestions.repositories.length === 0 ? (
                    <div className="px-3 py-8 text-center text-xs text-muted-foreground">
                      Start typing to search
                    </div>
                  ) : null}
                </>
              ) : loading ? (
                <div className="px-3 py-8 text-center text-xs text-muted-foreground" role="status">
                  Searching…
                </div>
              ) : error ? (
                <div className="px-3 py-8 text-center text-xs text-destructive" role="alert">
                  {error}
                </div>
              ) : !hasResults ? (
                <CommandEmpty>No matching results.</CommandEmpty>
              ) : (
                <>
                  {results.files.length > 0 ? (
                    <CommandGroup heading="Files">
                      {results.files.map((file) => {
                        const { directory, name } = resultDescription(file.path)
                        return (
                          <CommandItem
                            key={`${file.owner}/${file.repo}/${file.ref}/${file.path}`}
                            value={`file:${file.owner}/${file.repo}/${file.path}`}
                            onSelect={() => selectResult(fileSearchHref(file))}
                          >
                            <File />
                            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                              <span className="truncate font-medium">{name}</span>
                              <span className="truncate text-muted-foreground">
                                {`${file.owner}/${file.repo}${directory ? ` · ${directory}` : ''}`}
                              </span>
                            </span>
                          </CommandItem>
                        )
                      })}
                    </CommandGroup>
                  ) : null}

                  {results.owners.length > 0 ? (
                    <CommandGroup heading="Owners">
                      {results.owners.map((owner) => (
                        <CommandItem
                          key={`${owner.type}:${owner.id}`}
                          value={`owner:${owner.handle}`}
                          onSelect={() => selectResult(owner.href)}
                        >
                          {owner.type === 'organization' ? <Building2 /> : <UserRound />}
                          <span className="flex min-w-0 flex-1 items-baseline gap-2">
                            <span className="truncate font-medium">{owner.handle}</span>
                            {owner.name !== owner.handle ? (
                              <span className="truncate text-muted-foreground">{owner.name}</span>
                            ) : null}
                          </span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ) : null}

                  {results.owners.length > 0 && results.repositories.length > 0 ? (
                    <CommandSeparator />
                  ) : null}

                  {results.repositories.length > 0 ? (
                    <CommandGroup heading="Repositories">
                      {results.repositories.map((repo) => (
                        <CommandItem
                          key={repo.id}
                          value={`repository:${repo.namespace}/${repo.name}`}
                          onSelect={() => selectResult(repo.href)}
                        >
                          <BookMarked />
                          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                            <span className="flex min-w-0 items-center gap-1.5">
                              <span className="truncate font-medium">
                                {repo.namespace}/{repo.name}
                              </span>
                              {repo.isPrivate ? (
                                <LockKeyhole aria-label="Private repository" />
                              ) : null}
                            </span>
                            {repo.description ? (
                              <span className="truncate text-muted-foreground">{repo.description}</span>
                            ) : null}
                          </span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ) : null}
                </>
              )}
            </CommandList>
          </PopoverContent>
        </Popover>
      </Command>
    </div>
  )
}
